import React, { useEffect, useState, useRef, memo } from 'react';
import { MetricCard } from '../components/MetricCard';
import { ProgressBar } from '../components/ProgressBar';
import { ActivityIcon, AlertTriangleIcon, MessageSquareIcon, ShieldAlertIcon, ServerIcon, DollarSignIcon, CpuIcon, GpuIcon, MemoryStickIcon, ClockIcon, CheckCircleIcon, XCircleIcon, UsersIcon, DatabaseIcon, CircleIcon, HashIcon, CalculatorIcon, ChevronUpIcon, ChevronDownIcon, CalendarIcon, XIcon } from 'lucide-react';
export function Dashboard() {
  // State to track which sections are expanded
  const [expandedSections, setExpandedSections] = useState({
    coreMetrics: true,
    costTracking: true,
    resourceUtilization: true,
    guardrailsAlerts: true
  });
  // State for core metrics time range
  const [coreMetricsTimeRange, setCoreMetricsTimeRange] = useState('1hr');
  // State for custom time range modal
  const [showCustomTimeModal, setShowCustomTimeModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customStartTime, setCustomStartTime] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customEndTime, setCustomEndTime] = useState('');
  const [customRangeLabel, setCustomRangeLabel] = useState('');
  const modalRef = useRef(null);
  // Set default values for custom date/time pickers
  useEffect(() => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    setCustomEndDate(now.toISOString().split('T')[0]);
    setCustomEndTime(now.toTimeString().slice(0, 5));
    setCustomStartDate(yesterday.toISOString().split('T')[0]);
    setCustomStartTime(yesterday.toTimeString().slice(0, 5));
  }, []);
  // Handle click outside modal to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowCustomTimeModal(false);
      }
    }
    if (showCustomTimeModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomTimeModal]);
  // Apply custom time range
  const applyCustomTimeRange = () => {
    if (!customStartDate || !customStartTime || !customEndDate || !customEndTime) {
      return;
    }
    const startDateTime = new Date(`${customStartDate}T${customStartTime}:00`);
    const endDateTime = new Date(`${customEndDate}T${customEndTime}:00`);
    // Format dates for display
    const formatOptions = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    };
    const startFormatted = startDateTime.toLocaleDateString('en-US', formatOptions);
    const endFormatted = endDateTime.toLocaleDateString('en-US', formatOptions);
    setCustomRangeLabel(`${startFormatted} - ${endFormatted}`);
    setCoreMetricsTimeRange('custom');
    setShowCustomTimeModal(false);
  };
  // Toggle section expansion
  const toggleSection = section => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  // Mock data for different time ranges
  const timeRangeData = {
    '1m': {
      apiCallRate: '245',
      apiCallTrend: 5,
      failedRequests: '8',
      failedRequestsTrend: -2,
      tokensProcessed: '0.4M',
      tokensProcessedTrend: 7,
      activeConnections: '98',
      activeConnectionsTrend: 3,
      messagesSent: '1,932',
      messagesSentTrend: 2,
      successRate: '99.5%',
      successRateTrend: 0.1,
      costToday: '$245',
      costTodayTrend: 5,
      costLast: '$20',
      costLastTrend: 1,
      requests: '4,568',
      requestsTrend: 3,
      avgCost: '$0.06',
      avgCostTrend: -2,
      saasModels: [{
        id: 1,
        name: 'OpenAI',
        status: 'active',
        usage: 45
      }, {
        id: 2,
        name: 'Claude',
        status: 'active',
        usage: 15
      }],
      selfDeployedModels: [{
        id: 1,
        name: 'Endpoint 192.168.1.45',
        model: 'Llama 70b',
        status: 'active',
        usage: 18
      }, {
        id: 2,
        name: 'Endpoint 192.168.1.72',
        model: 'Deepseek',
        status: 'active',
        usage: 22
      }]
    },
    '10m': {
      apiCallRate: '645',
      apiCallTrend: 8,
      failedRequests: '15',
      failedRequestsTrend: -4,
      tokensProcessed: '1.2M',
      tokensProcessedTrend: 10,
      activeConnections: '125',
      activeConnectionsTrend: 5,
      messagesSent: '4,532',
      messagesSentTrend: 4,
      successRate: '99.7%',
      successRateTrend: 0.15,
      costToday: '$645',
      costTodayTrend: 8,
      costLast: '$60',
      costLastTrend: 1.5,
      requests: '12,568',
      requestsTrend: 6,
      avgCost: '$0.055',
      avgCostTrend: -1,
      saasModels: [{
        id: 1,
        name: 'OpenAI',
        status: 'active',
        usage: 42
      }, {
        id: 2,
        name: 'Claude',
        status: 'active',
        usage: 18
      }],
      selfDeployedModels: [{
        id: 1,
        name: 'Endpoint 192.168.1.45',
        model: 'Llama 70b',
        status: 'active',
        usage: 18
      }, {
        id: 2,
        name: 'Endpoint 192.168.1.72',
        model: 'Deepseek',
        status: 'active',
        usage: 22
      }]
    },
    '1hr': {
      apiCallRate: '1,245',
      apiCallTrend: 12,
      failedRequests: '23',
      failedRequestsTrend: -7,
      tokensProcessed: '2.4M',
      tokensProcessedTrend: 15,
      activeConnections: '142',
      activeConnectionsTrend: 8,
      messagesSent: '8,932',
      messagesSentTrend: 5,
      successRate: '99.8%',
      successRateTrend: 0.2,
      costToday: '$1,245',
      costTodayTrend: 15,
      costLast: '$120',
      costLastTrend: 2,
      requests: '24,568',
      requestsTrend: 8,
      avgCost: '$0.05',
      avgCostTrend: -3,
      saasModels: [{
        id: 1,
        name: 'OpenAI',
        status: 'active',
        usage: 40
      }, {
        id: 2,
        name: 'Claude',
        status: 'active',
        usage: 20
      }],
      selfDeployedModels: [{
        id: 1,
        name: 'Endpoint 192.168.1.45',
        model: 'Llama 70b',
        status: 'active',
        usage: 15
      }, {
        id: 2,
        name: 'Endpoint 192.168.1.72',
        model: 'Deepseek',
        status: 'active',
        usage: 25
      }]
    },
    today: {
      apiCallRate: '3,845',
      apiCallTrend: 18,
      failedRequests: '42',
      failedRequestsTrend: -5,
      tokensProcessed: '7.8M',
      tokensProcessedTrend: 22,
      activeConnections: '185',
      activeConnectionsTrend: 12,
      messagesSent: '25,432',
      messagesSentTrend: 15,
      successRate: '99.9%',
      successRateTrend: 0.3,
      costToday: '$3,845',
      costTodayTrend: 20,
      costLast: '$3,200',
      costLastTrend: 3,
      requests: '78,932',
      requestsTrend: 12,
      avgCost: '$0.048',
      avgCostTrend: -4,
      saasModels: [{
        id: 1,
        name: 'OpenAI',
        status: 'active',
        usage: 38
      }, {
        id: 2,
        name: 'Claude',
        status: 'active',
        usage: 22
      }],
      selfDeployedModels: [{
        id: 1,
        name: 'Endpoint 192.168.1.45',
        model: 'Llama 70b',
        status: 'active',
        usage: 16
      }, {
        id: 2,
        name: 'Endpoint 192.168.1.72',
        model: 'Deepseek',
        status: 'active',
        usage: 24
      }]
    },
    month: {
      apiCallRate: '5,842',
      apiCallTrend: 22,
      failedRequests: '64',
      failedRequestsTrend: -8,
      tokensProcessed: '12.3M',
      tokensProcessedTrend: 28,
      activeConnections: '215',
      activeConnectionsTrend: 15,
      messagesSent: '42,756',
      messagesSentTrend: 18,
      successRate: '99.8%',
      successRateTrend: 0.3,
      costToday: '$5,842',
      costTodayTrend: 25,
      costLast: '$4,500',
      costLastTrend: 4,
      requests: '124,568',
      requestsTrend: 15,
      avgCost: '$0.047',
      avgCostTrend: -5,
      saasModels: [{
        id: 1,
        name: 'OpenAI',
        status: 'active',
        usage: 37
      }, {
        id: 2,
        name: 'Claude',
        status: 'active',
        usage: 23
      }],
      selfDeployedModels: [{
        id: 1,
        name: 'Endpoint 192.168.1.45',
        model: 'Llama 70b',
        status: 'active',
        usage: 15
      }, {
        id: 2,
        name: 'Endpoint 192.168.1.72',
        model: 'Deepseek V3.1',
        status: 'active',
        usage: 25
      }]
    },
    custom: {
      apiCallRate: '2,542',
      apiCallTrend: 14,
      failedRequests: '31',
      failedRequestsTrend: -6,
      tokensProcessed: '4.6M',
      tokensProcessedTrend: 18,
      activeConnections: '156',
      activeConnectionsTrend: 10,
      messagesSent: '15,478',
      messagesSentTrend: 8,
      successRate: '99.7%',
      successRateTrend: 0.25,
      costToday: '$2,542',
      costTodayTrend: 17,
      costLast: '$180',
      costLastTrend: 2.5,
      requests: '42,756',
      requestsTrend: 10,
      avgCost: '$0.052',
      avgCostTrend: -3.5,
      saasModels: [{
        id: 1,
        name: 'OpenAI',
        status: 'active',
        usage: 39
      }, {
        id: 2,
        name: 'Claude',
        status: 'active',
        usage: 21
      }],
      selfDeployedModels: [{
        id: 1,
        name: 'Endpoint 192.168.1.45',
        model: 'Llama 70b',
        status: 'active',
        usage: 17
      }, {
        id: 2,
        name: 'Endpoint 192.168.1.72',
        model: 'Deepseek',
        status: 'active',
        usage: 23
      }]
    }
  };
  // Get current data based on selected time range
  const currentData = timeRangeData[coreMetricsTimeRange];
  // Mock data for the dashboard
  const alerts = [{
    id: 1,
    message: 'GPU 2 temperature exceeding threshold',
    timestamp: '2 min ago',
    severity: 'critical'
  }, {
    id: 2,
    message: 'API rate limit reached for key ABC123',
    timestamp: '15 min ago',
    severity: 'critical'
  }, {
    id: 3,
    message: 'Model swap completed: Llama-3-70B',
    timestamp: '45 min ago',
    severity: 'critical'
  }];
  const endpoints = [{
    id: 1,
    name: 'Endpoint 192.168.1.45 (Llama 70b)',
    status: 'active',
    gpu: 85,
    cpu: 45,
    memory: 78,
    latency: 230
  }, {
    id: 2,
    name: 'Endpoint 192.168.1.72 (Deepseek)',
    status: 'active',
    gpu: 65,
    cpu: 38,
    memory: 72,
    latency: 195
  }];
  return <div className="space-y-6 font-['Inter',sans-serif] text-white" data-id="element-1270">
      {/* Header with System Status */}
      <div className="flex items-center justify-between mb-2" data-id="element-1271">
        <h2 className="text-xl font-bold text-white" data-id="element-1272">
          System Health Monitoring
        </h2>
        <div className="flex items-center space-x-2" data-id="element-1273">
          <div className="px-3 py-1 bg-emerald-900 text-emerald-300 rounded-full text-sm font-medium flex items-center" data-id="element-1274">
            <span className="h-2 w-2 bg-emerald-400 rounded-full mr-2 animate-pulse" data-id="element-1275"></span>
            Active System
          </div>
        </div>
      </div>
      {/* SECTION 1: Core Metrics, Active Models, SaaS Metrics */}
      <div className="border border-[#222222] rounded-lg overflow-hidden" data-id="element-1276">
        {/* Section Header */}
        <div className="flex items-center justify-between bg-[#0F0F0F] px-4 py-3 cursor-pointer" onClick={() => toggleSection('coreMetrics')} data-id="element-1277">
          <div className="flex items-center" data-id="element-1278">
            <h3 className="font-medium text-gray-300 text-base" data-id="element-1279">
              Core Metrics
            </h3>
            {/* Time range selector */}
            <div className="ml-4 bg-[#1A1A1A] rounded-lg border border-[#333333] flex text-sm shadow-sm" data-id="element-1280">
              <button onClick={e => {
              e.stopPropagation();
              setCoreMetricsTimeRange('1m');
            }} className={`px-3 py-1 rounded-l-lg transition-colors ${coreMetricsTimeRange === '1m' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-[#222222]'}`} data-id="element-1281">
                1m
              </button>
              <button onClick={e => {
              e.stopPropagation();
              setCoreMetricsTimeRange('10m');
            }} className={`px-3 py-1 transition-colors ${coreMetricsTimeRange === '10m' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-[#222222]'}`} data-id="element-1282">
                10m
              </button>
              <button onClick={e => {
              e.stopPropagation();
              setCoreMetricsTimeRange('1hr');
            }} className={`px-3 py-1 transition-colors ${coreMetricsTimeRange === '1hr' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-[#222222]'}`} data-id="element-1283">
                1hr
              </button>
              <button onClick={e => {
              e.stopPropagation();
              setCoreMetricsTimeRange('today');
            }} className={`px-3 py-1 transition-colors ${coreMetricsTimeRange === 'today' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-[#222222]'}`} data-id="element-1284">
                Today
              </button>
              <button onClick={e => {
              e.stopPropagation();
              setCoreMetricsTimeRange('month');
            }} className={`px-3 py-1 transition-colors ${coreMetricsTimeRange === 'month' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-[#222222]'}`} data-id="element-1285">
                Current Month
              </button>
              <button onClick={e => {
              e.stopPropagation();
              setShowCustomTimeModal(true);
            }} className={`px-3 py-1 rounded-r-lg transition-colors flex items-center ${coreMetricsTimeRange === 'custom' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-[#222222]'}`} data-id="element-1286">
                <CalendarIcon size={14} className="mr-1" data-id="element-1287" />
                {coreMetricsTimeRange === 'custom' ? 'Custom' : 'Custom'}
              </button>
            </div>
          </div>
          <button className="text-gray-400 hover:text-gray-200" data-id="element-1288">
            {expandedSections.coreMetrics ? <ChevronUpIcon size={20} data-id="element-1289" /> : <ChevronDownIcon size={20} data-id="element-1290" />}
          </button>
        </div>
        {/* Section Content */}
        <div className={`transition-all duration-300 overflow-hidden ${expandedSections.coreMetrics ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-1291">
          <div className="p-4 space-y-6 bg-black" data-id="element-1292">
            {/* Display selected time range */}
            <div className="text-sm text-gray-400" data-id="element-1293">
              Showing data for:{' '}
              <span className="font-medium text-gray-300" data-id="element-1294">
                {coreMetricsTimeRange === 'today' ? 'Today' : coreMetricsTimeRange === 'month' ? 'Current Month' : coreMetricsTimeRange === 'custom' ? customRangeLabel : coreMetricsTimeRange}
              </span>
            </div>
            {/* Core Metrics and Active Models in side-by-side layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1295">
              {/* Core Metrics Section - Takes 2 columns */}
              <div className="lg:col-span-2 space-y-6" data-id="element-1296">
                {/* First row of metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1297">
                  <div className="transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1298">
                    <MetricCard title="API Call Rate" value={currentData.apiCallRate} icon={<ActivityIcon size={18} className="text-white" data-id="element-1300" />} trend="up" trendValue={`${currentData.apiCallTrend}% from last ${coreMetricsTimeRange === 'today' ? 'day' : coreMetricsTimeRange === 'custom' ? 'period' : coreMetricsTimeRange}`} data-id="element-1299" />
                  </div>
                  <div className="transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1301">
                    <MetricCard title="Failed API Requests" value={currentData.failedRequests} icon={<AlertTriangleIcon size={18} className="text-white" data-id="element-1303" />} trend="down" trendValue={`${currentData.failedRequestsTrend}% from last ${coreMetricsTimeRange === 'today' ? 'day' : coreMetricsTimeRange === 'custom' ? 'period' : coreMetricsTimeRange}`} data-id="element-1302" />
                  </div>
                  <div className="transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1304">
                    <MetricCard title="Tokens Processed" value={currentData.tokensProcessed} icon={<DatabaseIcon size={18} className="text-white" data-id="element-1306" />} trend="up" trendValue={`${currentData.tokensProcessedTrend}% from last ${coreMetricsTimeRange === 'today' ? 'day' : coreMetricsTimeRange === 'custom' ? 'period' : coreMetricsTimeRange}`} data-id="element-1305" />
                  </div>
                </div>
                {/* Second row of metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1307">
                  <div className="transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1308">
                    <MetricCard title="Active Connections" value={currentData.activeConnections} icon={<UsersIcon size={18} className="text-white" data-id="element-1310" />} trend="up" trendValue={`${currentData.activeConnectionsTrend}% from last ${coreMetricsTimeRange === 'today' ? 'day' : coreMetricsTimeRange === 'custom' ? 'period' : coreMetricsTimeRange}`} data-id="element-1309" />
                  </div>
                  <div className="transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1311">
                    <MetricCard title="Messages Sent" value={currentData.messagesSent} icon={<MessageSquareIcon size={18} className="text-white" data-id="element-1313" />} trend="up" trendValue={`${currentData.messagesSentTrend}% from last ${coreMetricsTimeRange === 'today' ? 'day' : coreMetricsTimeRange === 'custom' ? 'period' : coreMetricsTimeRange}`} data-id="element-1312" />
                  </div>
                </div>
              </div>
              {/* Active Models Section - Takes 1 column */}
              <div className="transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1314">
                <div className="bg-[#0F0F0F] rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.3)] p-5 h-full border border-[#222222]" data-id="element-1315">
                  <h3 className="text-base font-medium text-gray-200 mb-4 pb-2 border-b border-[#333333]" data-id="element-1316">
                    Active Models - Usage
                  </h3>
                  <div className="space-y-5" data-id="element-1317">
                    {/* SaaS Models */}
                    <div data-id="element-1318">
                      <h4 className="text-sm font-medium text-gray-200 mb-3" data-id="element-1319">
                        SaaS
                      </h4>
                      <div className="space-y-4" data-id="element-1320">
                        {currentData.saasModels.map(model => <div key={model.id} className="flex items-center justify-between" data-id="element-1321">
                            <div className="flex items-center" data-id="element-1322">
                              <CircleIcon size={8} className="text-emerald-400 mr-2" data-id="element-1323" />
                              <span className="text-sm text-gray-300" data-id="element-1324">
                                {model.name}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2" data-id="element-1325">
                              <div className="w-32 bg-[#222222] rounded-full h-2" data-id="element-1326">
                                <div className="bg-emerald-500 h-2 rounded-full" style={{
                              width: `${model.usage}%`
                            }} data-id="element-1327"></div>
                              </div>
                              <span className="text-sm font-medium text-gray-200" data-id="element-1328">
                                {model.usage}%
                              </span>
                            </div>
                          </div>)}
                      </div>
                    </div>
                    {/* Self-Deployed Models */}
                    <div data-id="element-1329">
                      <h4 className="text-sm font-medium text-gray-200 mb-3" data-id="element-1330">
                        Self-Deployed
                      </h4>
                      <div className="space-y-4" data-id="element-1331">
                        {currentData.selfDeployedModels.map(model => <div key={model.id} className="flex items-center justify-between" data-id="element-1332">
                            <div className="flex items-center" data-id="element-1333">
                              <CircleIcon size={8} className={model.status === 'active' ? 'text-emerald-400' : 'text-rose-500'} fill={model.status === 'active' ? '#34d399' : '#f43f5e'} strokeWidth={0} className="mr-2" data-id="element-1334" />
                              <span className="text-sm text-gray-300" data-id="element-1335">
                                {model.model}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2" data-id="element-1336">
                              <div className="w-32 bg-[#222222] rounded-full h-2" data-id="element-1337">
                                <div className="bg-blue-500 h-2 rounded-full" style={{
                              width: `${model.usage}%`
                            }} data-id="element-1338"></div>
                              </div>
                              <span className="text-sm font-medium text-gray-200" data-id="element-1339">
                                {model.usage}%
                              </span>
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* SaaS Metrics Section */}
            <div data-id="element-1340">
              <h3 className="text-base font-medium text-gray-200 mb-4 pb-2 border-b border-[#333333]" data-id="element-1341">
                SaaS Metrics
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1342">
                <div className="transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1343">
                  <MetricCard title="Success Rate" value={currentData.successRate} icon={<CheckCircleIcon size={18} className="text-emerald-400" data-id="element-1345" />} trend="up" trendValue={`${currentData.successRateTrend}% from last ${coreMetricsTimeRange === 'today' ? 'day' : coreMetricsTimeRange === 'custom' ? 'period' : coreMetricsTimeRange}`} data-id="element-1344" />
                </div>
                <div className="transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1346">
                  <MetricCard title="$ 1hr Cost" value={currentData.costLast} icon={<DollarSignIcon size={18} className="text-white" data-id="element-1348" />} trend="neutral" trendValue={`${currentData.costLastTrend}% change`} data-id="element-1347" />
                </div>
                <div className="transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1349">
                  <MetricCard title="# of Requests" value={currentData.requests} icon={<HashIcon size={18} className="text-white" data-id="element-1351" />} trend="up" trendValue={`${currentData.requestsTrend}% from last ${coreMetricsTimeRange === 'today' ? 'day' : coreMetricsTimeRange === 'custom' ? 'period' : coreMetricsTimeRange}`} data-id="element-1350" />
                </div>
                <div className="transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1352">
                  <MetricCard title="Avg Cost / Request" value={currentData.avgCost} icon={<CalculatorIcon size={18} className="text-white" data-id="element-1354" />} trend="down" trendValue={`${currentData.avgCostTrend}% from last ${coreMetricsTimeRange === 'today' ? 'day' : coreMetricsTimeRange === 'custom' ? 'period' : coreMetricsTimeRange}`} data-id="element-1353" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* NEW SECTION: Cost Tracking & Budget */}
      <div className="border border-[#222222] rounded-lg overflow-hidden" data-id="element-1355">
        {/* Section Header */}
        <div className="flex items-center justify-between bg-[#0F0F0F] px-4 py-3 cursor-pointer" onClick={() => toggleSection('costTracking')} data-id="element-1356">
          <h3 className="font-medium text-gray-300 text-base" data-id="element-1357">
            Cost Tracking & Budget
          </h3>
          <button className="text-gray-400 hover:text-gray-200" data-id="element-1358">
            {expandedSections.costTracking ? <ChevronUpIcon size={20} data-id="element-1359" /> : <ChevronDownIcon size={20} data-id="element-1360" />}
          </button>
        </div>
        {/* Section Content */}
        <div className={`transition-all duration-300 overflow-hidden ${expandedSections.costTracking ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-1361">
          <div className="p-4 bg-black" data-id="element-1362">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1363">
              {/* Current Consumption */}
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222222] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1364">
                <h3 className="text-sm font-medium text-gray-400 mb-2 flex items-center" data-id="element-1365">
                  <DollarSignIcon size={16} className="mr-2 text-green-400" data-id="element-1366" />
                  Current Consumption
                </h3>
                <div className="flex items-baseline" data-id="element-1367">
                  <span className="text-2xl font-semibold text-white" data-id="element-1368">
                    $1.38
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-2" data-id="element-1369">
                  0% of $633.00 total budget
                </p>
                <div className="mt-2" data-id="element-1370">
                  <div className="h-2 w-full bg-[#222222] rounded-full" data-id="element-1371">
                    <div className="h-2 bg-green-500 rounded-full" style={{
                    width: '0.2%'
                  }} data-id="element-1372"></div>
                  </div>
                </div>
              </div>
              {/* Month to Date */}
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222222] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1373">
                <h3 className="text-sm font-medium text-gray-400 mb-2 flex items-center" data-id="element-1374">
                  <CalendarIcon size={16} className="mr-2 text-blue-400" data-id="element-1375" />
                  Month to Date
                </h3>
                <div className="flex items-baseline" data-id="element-1376">
                  <span className="text-2xl font-semibold text-white" data-id="element-1377">
                    $0.00
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-2" data-id="element-1378">0% vs yesterday</p>
                <div className="mt-2" data-id="element-1379">
                  <div className="h-2 w-full bg-[#222222] rounded-full" data-id="element-1380">
                    <div className="h-2 bg-blue-500 rounded-full" style={{
                    width: '0%'
                  }} data-id="element-1381"></div>
                  </div>
                </div>
              </div>
              {/* Daily Average */}
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222222] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1382">
                <h3 className="text-sm font-medium text-gray-400 mb-2 flex items-center" data-id="element-1383">
                  <ActivityIcon size={16} className="mr-2 text-purple-400" data-id="element-1384" />
                  Daily Average
                </h3>
                <div className="flex items-baseline" data-id="element-1385">
                  <span className="text-2xl font-semibold text-white" data-id="element-1386">
                    $0.00
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-2" data-id="element-1387">
                  Projected: $0.00/mo
                </p>
                <div className="mt-2" data-id="element-1388">
                  <div className="h-2 w-full bg-[#222222] rounded-full" data-id="element-1389">
                    <div className="h-2 bg-purple-500 rounded-full" style={{
                    width: '0%'
                  }} data-id="element-1390"></div>
                  </div>
                </div>
              </div>
              {/* Budget Remaining */}
              <div className="bg-[#0F0F0F] rounded-xl p-4 border border-[#222222] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1391">
                <h3 className="text-sm font-medium text-gray-400 mb-2 flex items-center" data-id="element-1392">
                  <DollarSignIcon size={16} className="mr-2 text-emerald-400" data-id="element-1393" />
                  Budget Remaining
                </h3>
                <div className="flex items-baseline" data-id="element-1394">
                  <span className="text-2xl font-semibold text-white" data-id="element-1395">
                    $631.62
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-2" data-id="element-1396">100% remaining</p>
                <div className="mt-2" data-id="element-1397">
                  <div className="h-2 w-full bg-[#222222] rounded-full" data-id="element-1398">
                    <div className="h-2 bg-emerald-500 rounded-full" style={{
                    width: '100%'
                  }} data-id="element-1399"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* SECTION 2: Self-Deployed Resource Utilization */}
      <div className="border border-[#222222] rounded-lg overflow-hidden" data-id="element-1400">
        {/* Section Header */}
        <div className="flex items-center justify-between bg-[#0F0F0F] px-4 py-3 cursor-pointer" onClick={() => toggleSection('resourceUtilization')} data-id="element-1401">
          <h3 className="font-medium text-gray-300 text-base" data-id="element-1402">
            Self-Deployed Resource Utilization
          </h3>
          <button className="text-gray-400 hover:text-gray-200" data-id="element-1403">
            {expandedSections.resourceUtilization ? <ChevronUpIcon size={20} data-id="element-1404" /> : <ChevronDownIcon size={20} data-id="element-1405" />}
          </button>
        </div>
        {/* Section Content */}
        <div className={`transition-all duration-300 overflow-hidden bg-black ${expandedSections.resourceUtilization ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-1406">
          <div className="p-4" data-id="element-1407">
            <div className="bg-[#0F0F0F] rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.3)] p-5 border border-[#222222] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1408">
              <div className="space-y-6" data-id="element-1409">
                {endpoints.map(endpoint => <div key={endpoint.id} className="space-y-3" data-id="element-1410">
                    <div className="flex items-center" data-id="element-1411">
                      <div className={`h-2 w-2 rounded-full mr-2 ${endpoint.status === 'active' ? 'bg-emerald-400' : 'bg-gray-600'}`} data-id="element-1412"></div>
                      <h4 className="text-sm font-medium text-gray-200" data-id="element-1413">
                        {endpoint.name}
                      </h4>
                    </div>
                    {endpoint.status === 'active' ? <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1414">
                        <div className="bg-[#121212] rounded-lg p-3 border border-[#333333]" data-id="element-1415">
                          <div className="flex items-center justify-between mb-2" data-id="element-1416">
                            <h5 className="text-sm font-medium text-gray-400 flex items-center" data-id="element-1417">
                              <GpuIcon size={14} className="mr-1 text-purple-400" data-id="element-1418" />{' '}
                              GPU
                            </h5>
                            <span className="text-sm font-semibold text-gray-200" data-id="element-1419">
                              {endpoint.gpu}%
                            </span>
                          </div>
                          <ProgressBar value={endpoint.gpu} max={100} color={endpoint.gpu > 80 ? '#f43f5e' : 'purple'} data-id="element-1420" />
                        </div>
                        <div className="bg-[#121212] rounded-lg p-3 border border-[#333333]" data-id="element-1421">
                          <div className="flex items-center justify-between mb-2" data-id="element-1422">
                            <h5 className="text-sm font-medium text-gray-400 flex items-center" data-id="element-1423">
                              <CpuIcon size={14} className="mr-1 text-blue-400" data-id="element-1424" />{' '}
                              CPU
                            </h5>
                            <span className="text-sm font-semibold text-gray-200" data-id="element-1425">
                              {endpoint.cpu}%
                            </span>
                          </div>
                          <ProgressBar value={endpoint.cpu} max={100} color="blue" data-id="element-1426" />
                        </div>
                        <div className="bg-[#121212] rounded-lg p-3 border border-[#333333]" data-id="element-1427">
                          <div className="flex items-center justify-between mb-2" data-id="element-1428">
                            <h5 className="text-sm font-medium text-gray-400 flex items-center" data-id="element-1429">
                              <MemoryStickIcon size={14} className="mr-1 text-emerald-400" data-id="element-1430" />{' '}
                              Memory
                            </h5>
                            <span className="text-sm font-semibold text-gray-200" data-id="element-1431">
                              {endpoint.memory}%
                            </span>
                          </div>
                          <ProgressBar value={endpoint.memory} max={100} color="gradient" data-id="element-1432" />
                        </div>
                        <div className="bg-[#121212] rounded-lg p-3 border border-[#333333]" data-id="element-1433">
                          <div className="flex items-center justify-between mb-2" data-id="element-1434">
                            <h5 className="text-sm font-medium text-gray-400 flex items-center" data-id="element-1435">
                              <ClockIcon size={14} className="mr-1 text-yellow-400" data-id="element-1436" />{' '}
                              Latency
                            </h5>
                            <span className="text-sm font-semibold text-gray-200" data-id="element-1437">
                              {endpoint.latency}ms
                            </span>
                          </div>
                          <ProgressBar value={endpoint.latency} max={500} color={endpoint.latency > 300 ? '#f43f5e' : endpoint.latency > 200 ? 'yellow' : 'gradient'} data-id="element-1438" />
                        </div>
                      </div> : <div className="text-gray-500 italic" data-id="element-1439">
                        This endpoint is currently inactive
                      </div>}
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* SECTION 3: Guardrails and Recent Critical Alerts */}
      <div className="border border-[#222222] rounded-lg overflow-hidden" data-id="element-1440">
        {/* Section Header */}
        <div className="flex items-center justify-between bg-[#0F0F0F] px-4 py-3 cursor-pointer" onClick={() => toggleSection('guardrailsAlerts')} data-id="element-1441">
          <h3 className="font-medium text-gray-300 text-base" data-id="element-1442">
            Guardrails & Alerts
          </h3>
          <button className="text-gray-400 hover:text-gray-200" data-id="element-1443">
            {expandedSections.guardrailsAlerts ? <ChevronUpIcon size={20} data-id="element-1444" /> : <ChevronDownIcon size={20} data-id="element-1445" />}
          </button>
        </div>
        {/* Section Content */}
        <div className={`transition-all duration-300 overflow-hidden bg-black ${expandedSections.guardrailsAlerts ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-1446">
          <div className="p-4" data-id="element-1447">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1448">
              {/* Guardrails */}
              <div className="bg-[#0F0F0F] rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.3)] p-5 border border-[#222222] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1449">
                <h3 className="text-base font-medium text-gray-200 mb-4 pb-2 border-b border-[#333333]" data-id="element-1450">
                  Guardrails
                </h3>
                <div className="space-y-4" data-id="element-1451">
                  <div className="flex items-center justify-between p-3 bg-red-900/30 rounded-lg border border-red-900/50" data-id="element-1452">
                    <div className="flex items-center" data-id="element-1453">
                      <ShieldAlertIcon size={16} className="text-red-400 mr-2" data-id="element-1454" />
                      <span className="text-sm font-medium text-red-300" data-id="element-1455">
                        Harmful content
                      </span>
                    </div>
                    <span className="text-sm font-medium bg-red-900/50 text-red-300 px-2 py-1 rounded-md" data-id="element-1456">
                      12 blocks
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-yellow-900/30 rounded-lg border border-yellow-900/50" data-id="element-1457">
                    <div className="flex items-center" data-id="element-1458">
                      <ShieldAlertIcon size={16} className="text-yellow-400 mr-2" data-id="element-1459" />
                      <span className="text-sm font-medium text-yellow-300" data-id="element-1460">
                        PII data
                      </span>
                    </div>
                    <span className="text-sm font-medium bg-yellow-900/50 text-yellow-300 px-2 py-1 rounded-md" data-id="element-1461">
                      8 blocks
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-900/30 rounded-lg border border-blue-900/50" data-id="element-1462">
                    <div className="flex items-center" data-id="element-1463">
                      <ShieldAlertIcon size={16} className="text-blue-400 mr-2" data-id="element-1464" />
                      <span className="text-sm font-medium text-blue-300" data-id="element-1465">
                        Code execution
                      </span>
                    </div>
                    <span className="text-sm font-medium bg-blue-900/50 text-blue-300 px-2 py-1 rounded-md" data-id="element-1466">
                      4 blocks
                    </span>
                  </div>
                </div>
              </div>
              {/* Recent Critical Alerts */}
              <div className="bg-[#0F0F0F] rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.3)] p-5 border border-[#222222] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-md" data-id="element-1467">
                <h3 className="text-base font-medium text-gray-200 mb-4 pb-2 border-b border-[#333333]" data-id="element-1468">
                  Recent Critical Alerts
                </h3>
                <div className="space-y-4" data-id="element-1469">
                  {alerts.map(alert => <div key={alert.id} className="flex items-start p-3 bg-[#121212] rounded-lg border border-[#333333]" data-id="element-1470">
                      <div className="mt-1 h-3 w-3 rounded-full mr-3 bg-red-500 animate-pulse" data-id="element-1471"></div>
                      <div className="flex-1" data-id="element-1472">
                        <p className="text-sm font-medium text-gray-200" data-id="element-1473">
                          {alert.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-1474">
                          {alert.timestamp}
                        </p>
                      </div>
                    </div>)}
                </div>
                <button className="mt-4 text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors duration-200 flex items-center" data-id="element-1475">
                  View more
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1476">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-1477" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Custom Time Range Modal */}
      {showCustomTimeModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1478">
          <div ref={modalRef} className="bg-[#0F0F0F] rounded-lg shadow-xl border border-[#333333] w-full max-w-md p-5" data-id="element-1479">
            <div className="flex items-center justify-between mb-4" data-id="element-1480">
              <h3 className="text-lg font-medium text-white flex items-center" data-id="element-1481">
                <CalendarIcon size={18} className="mr-2 text-blue-400" data-id="element-1482" />
                Custom Time Range
              </h3>
              <button onClick={() => setShowCustomTimeModal(false)} className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-[#222222]" data-id="element-1483">
                <XIcon size={18} data-id="element-1484" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-1485">
              <div data-id="element-1486">
                <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1487">
                  Start Date & Time
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-1488">
                  <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} className="bg-[#1A1A1A] border border-[#333333] rounded-md p-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-1489" />
                  <input type="time" value={customStartTime} onChange={e => setCustomStartTime(e.target.value)} className="bg-[#1A1A1A] border border-[#333333] rounded-md p-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-1490" />
                </div>
              </div>
              <div data-id="element-1491">
                <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1492">
                  End Date & Time
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-1493">
                  <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} className="bg-[#1A1A1A] border border-[#333333] rounded-md p-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-1494" />
                  <input type="time" value={customEndTime} onChange={e => setCustomEndTime(e.target.value)} className="bg-[#1A1A1A] border border-[#333333] rounded-md p-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-1495" />
                </div>
              </div>
              <div className="pt-4 border-t border-[#222222] flex justify-end space-x-3" data-id="element-1496">
                <button onClick={() => setShowCustomTimeModal(false)} className="px-4 py-2 bg-[#222222] text-gray-300 rounded-md hover:bg-[#333333] transition-colors" data-id="element-1497">
                  Cancel
                </button>
                <button onClick={applyCustomTimeRange} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-1498">
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* CSS for background patterns and animations */}
      <style jsx global data-id="element-1499">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        /* Background pattern for charts */
        .bg-pattern {
          background-image:
            linear-gradient(rgba(30, 30, 30, 0.8) 1px, transparent 1px),
            linear-gradient(
              to right,
              rgba(30, 30, 30, 0.8) 1px,
              transparent 1px
            );
          background-size: 20px 20px;
        }
        /* Tooltip styles */
        [data-tooltip] {
          position: relative;
          cursor: pointer;
        }
        [data-tooltip]:before {
          content: attr(data-tooltip);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          padding: 6px 10px;
          border-radius: 6px;
          background: #0f172a;
          color: white;
          font-size: 12px;
          white-space: nowrap;
          opacity: 0;
          visibility: hidden;
          transition:
            opacity 0.2s,
            visibility 0.2s;
        }
        [data-tooltip]:hover:before {
          opacity: 1;
          visibility: visible;
        }
      `}</style>
    </div>;
}